import { Center } from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Badge from '../../../../../common/src/components/Badge';
import { mq } from '../../../../../common/src/utils/styledComponentsUtils';
import LoadingSpinner from '../../../../../common/src/components/LoadingSpinner';
import { useQuote, useTopProduct } from '../../../hooks/useQuoteQueries';
import { RootState } from '../../../store/redux/reducers/rootReducer';
import { getOfferForProductAndQuote } from '../../../services/offersService';
import SingleSelectedOfferInfo from './SingleSelectedOfferInfo';
import SingleSelectedOfferSummary from './SingleSelectedOfferSummary';

interface Props {
  quoteHashId: string;
  riskObject: string;
}

const SingleSelectedOffer: React.FC<React.PropsWithChildren<Props>> = ({
  quoteHashId,
  riskObject,
}) => {
  const { t } = useTranslation();
  const { preferredOffers } = useSelector((state: RootState) => state.offers);

  const { data: quote } = useQuote(riskObject, quoteHashId);
  const { data: product } = useTopProduct(riskObject, quoteHashId);

  if (!product)
    return (
      <SingleOffer>
        <Center p="32" w="100%">
          <LoadingSpinner />
        </Center>
      </SingleOffer>
    );

  const offer = getOfferForProductAndQuote(product, quote);

  return (
    <SingleOffer preffered={preferredOffers[riskObject] === offer.hash_id}>
      {preferredOffers[riskObject] === offer.hash_id && (
        <StyledBadge type="PRIMARY">{t('Preferred offer')}</StyledBadge>
      )}
      <SingleSelectedOfferInfo offer={offer} riskObject={riskObject} />
      <SingleSelectedOfferSummary offer={offer} riskObject={riskObject} />
    </SingleOffer>
  );
};

const StyledBadge = styled(Badge)`
  position: absolute;
  transform: translate(-50%, 50%);
  left: 50%;
  bottom: 100%;
  text-align: center;
`;

const SingleOffer = styled.div<{ preffered?: boolean }>`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid
    ${({ preffered, theme }) =>
      preffered ? theme.primary[500] : theme.gray[50]};
  box-shadow: 0 0 2px rgba(40, 41, 61, 0.04), 0 4px 8px rgba(96, 97, 112, 0.16);
  border-radius: 6px;
  position: relative;

  & .LabelValue-label {
    font-size: 1.6rem;
    margin-bottom: 0.4rem;
  }

  & .LabelValue-value {
    font-weight: 700;
    font-size: 1.8rem;
  }

  ${mq.laptop} {
    flex-direction: row;
  }
`;

export default SingleSelectedOffer;
